import React from 'react';
import { TypedMemo } from '../../common/types';
import { ConnectionInsight, ServiceConnection } from '../../../redux/api/fymAPI.types';
import { ConnectionCard } from './ConnectionCard';
import { ConnectionCardEarlyAccess } from './earlyAccess/ConnectionCardEarlyAccess';
import { SessionCaptureConnectionCard } from './sessionCapture/SessionCaptureConnectionCard';
import { isSessionCaptureCard } from './sessionCapture/sessionCaptureCardState';
import { SoundCloudConnectionCard } from './soundCloud/SoundCloudConnectionCard';
import { isSoundCloudCard } from './soundCloud/soundCloudCardState';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- shared stylesheets; each part uses its own slice of them */
import styles from './ConnectionsPanel.module.scss';
import earlyAccessStyles from './earlyAccess/EarlyAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface ConnectionsDeckProps {
  title: string;
  subtitle: string;
  connections: ServiceConnection[];
  insights: Record<string, ConnectionInsight>;
  onEditProfileUrl: (integrationType: string) => void;
  onConnect: (integrationType: string) => void;
  onSync: (integrationType: string) => void;
  onDisconnect: (integrationType: string) => void;
  /** Opens the artist picker of a login that runs in the app (Ditto Music). */
  onChooseArtist?: (integrationType: string) => void;
  isBusy: boolean;
  now?: number;
  /** The Spotify for Artists card shows the Spotify Canvas switch (design spotify-canvas b5). */
  showCanvasSwitch?: boolean;
}

/**
 * One section of the service strip: a small heading, then a card per service.
 * A source whose login runs inside the phone app (Ditto Music) draws its own
 * card with the phone handoff. A service with no sign-in yet draws the "Coming
 * soon" card; a built one the connection card.
 *
 * design: connector-waitlist a1 (Streaming services, Distributors), your-intelligence k1
 */
const ConnectionsDeckComponent: React.FC<ConnectionsDeckProps> = ({
  title,
  subtitle,
  connections,
  insights,
  onEditProfileUrl,
  onConnect,
  onSync,
  onDisconnect,
  onChooseArtist,
  isBusy,
  now,
  showCanvasSwitch = false,
}) => (
  <section aria-label={title}>
    <div className={earlyAccessStyles.sectionRow}>
      <span className={earlyAccessStyles.sectionTitle}>{title}</span>
      <span className={earlyAccessStyles.sectionSub}>{withBrandWord(subtitle)}</span>
    </div>
    <div className={styles.connections}>
      {connections.map((connection) => {
        if (isSessionCaptureCard(connection)) {
          return (
            <SessionCaptureConnectionCard
              key={connection.integrationType}
              connection={connection}
              onConnect={onConnect}
              onDisconnect={onDisconnect}
              onChooseArtist={onChooseArtist}
              isBusy={isBusy}
              now={now}
            />
          );
        }
        // design: soundcloud-connector a — its sign-in leaves for MusicAPI and lands on Channels.
        if (isSoundCloudCard(connection)) {
          return (
            <SoundCloudConnectionCard
              key={connection.integrationType}
              connection={connection}
              onSync={onSync}
              onDisconnect={onDisconnect}
              isBusy={isBusy}
              now={now}
            />
          );
        }
        return connection.earlyAccess ? (
          <ConnectionCardEarlyAccess
            key={connection.integrationType}
            connection={connection}
            insight={insights[connection.integrationType]}
            onEditProfileUrl={onEditProfileUrl}
            isBusy={isBusy}
          />
        ) : (
          <ConnectionCard
            key={connection.integrationType}
            connection={connection}
            insight={insights[connection.integrationType]}
            onEditProfileUrl={onEditProfileUrl}
            onConnect={onConnect}
            onSync={onSync}
            onDisconnect={onDisconnect}
            isBusy={isBusy}
            now={now}
            showCanvasSwitch={showCanvasSwitch}
          />
        );
      })}
    </div>
  </section>
);

export const ConnectionsDeck = TypedMemo(ConnectionsDeckComponent);
